// src/pages/Vendor/Apply/components/Step3Review.tsx
import React from 'react';
import { CheckCircle, Store, FileText, AlertCircle, XCircle } from 'lucide-react';

interface Step3ReviewProps {
  data: any;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  submitError: string | null;
}

const Step3Review: React.FC<Step3ReviewProps> = ({ data, onBack, onSubmit, isSubmitting, submitError }) => {
  const formatAddress = (address: any) => {
    if (!address) return 'Not provided';
    const parts = [
      address.house_number,
      address.address_line_1,
      address.address_line_2,
      address.city_name
    ].filter(Boolean);
    return parts.join(', ') || 'Not provided';
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Review Application</h2>
      <p className="text-sm text-gray-500 mb-6">Please review your information before submitting</p>

      <div className="space-y-4">
        <Section title="Business Information" icon={Store}>
          <InfoRow label="Business Name" value={data.business_name} />
          <InfoRow label="Business Email" value={data.business_email} />
          <InfoRow label="Phone" value={data.business_phone} />
          <InfoRow label="Address" value={formatAddress(data.business_address)} />
          <InfoRow label="Business Type" value={data.business_type} />
          {data.website && <InfoRow label="Website" value={data.website} />}
          {data.description && <InfoRow label="Description" value={data.description} />}
          <InfoRow
            label="Location Verified"
            value={
              data.business_address?.captured_location ? (
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  Yes
                </span>
              ) : (
                <span className="flex items-center gap-1 text-gray-400">
                  <XCircle className="h-4 w-4" />
                  Not verified
                </span>
              )
            }
          />
        </Section>

        <Section title="Legal & Documentation" icon={FileText}>
          <InfoRow label="Registration Number" value={data.registration_number} />
          <InfoRow label="Tax ID" value={data.tax_id} />
          <InfoRow
            label="Documents"
            value={[
              data.registration_file?.name || 'Registration certificate uploaded',
              data.address_proof?.name || 'Address proof uploaded'
            ].join(' • ')}
          />
        </Section>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-green-800">Ready to Submit</p>
            <p className="text-sm text-green-700">
              Our team will review your application within 1-3 business days.
              You'll receive an email notification once approved.
            </p>
          </div>
        </div>
      </div>

      {/* Show error if present */}
      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 mt-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Submission Failed</p>
              <p className="text-sm text-red-700">{submitError}</p>
            </div>
          </div>
        </div>
      )}
      <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="px-6 py-2.5 text-gray-600 hover:text-gray-900 font-medium transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={onSubmit}
          disabled={isSubmitting}
          className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors flex items-center gap-2"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              Submitting...
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4" />
              Submit Application
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; icon: React.ElementType; children: React.ReactNode }> = ({
  title,
  icon: Icon,
  children,
}) => (
  <div className="border border-gray-200 rounded-lg p-4">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="h-4 w-4 text-gray-500" />
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    </div>
    <div className="space-y-1">{children}</div>
  </div>
);

const InfoRow: React.FC<{ label: string; value: string | React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between py-1 text-sm border-b border-gray-50 last:border-0">
    <span className="text-gray-500">{label}</span>
    <span className="text-gray-900 font-medium truncate max-w-[60%]">
      {typeof value === 'string' ? value : value}
    </span>
  </div>
);
export default Step3Review;